import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import Loader from 'components/shared/loader/loader.component';
import InfoText from 'components/shared/info-text.component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import DeclineRequestDialog from 'components/shared/decline-request-dialog/decline-request-dialog.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import {
    approveSupplierRequestTemplate,
    declineSupplierRequestTemplate,
    incomingSupplierRequestsTemplate,
} from 'apollo/templates/supplier-request';
import { SupplierRequest, SupplierRequestStatus } from 'models/supplier-request';
import SupplierRequestTable from './supplier-request-table.component';

/** Pending requests first, then the history; each group newest first (the server's order). */
const pendingFirst = (requests: SupplierRequest[]) => [
    ...requests.filter((request) => request.status === SupplierRequestStatus.PENDING),
    ...requests.filter((request) => request.status !== SupplierRequestStatus.PENDING),
];

/**
 * DD-3662: labs that asked to use this lab as a supplier. A technician admin approves
 * (the lab becomes a customer; then "Show supplier settings?") or declines.
 */
const IncomingSupplierRequests: FC = () => {
    const { t: translate } = useTranslation(['client-directory', 'common']);
    const navigate = useNavigate();
    const { showBoundary } = useDenthubErrorBoundary();
    const isAdmin = permissions.isTechnicianAdmin(currentUserVar()?.role);
    const [toDecline, setToDecline] = useState<SupplierRequest>();
    const [approved, setApproved] = useState<SupplierRequest>();

    const { data, loading: isLoading } = useQuery(incomingSupplierRequestsTemplate, {
        fetchPolicy: 'cache-and-network',
        onError: (error) => showBoundary(error),
    });
    // Refetch by name: the list here and the pending count on the tab.
    const [approve, { loading: isApproving }] = useMutation(approveSupplierRequestTemplate, {
        refetchQueries: ['incomingSupplierRequests'],
        onCompleted: ({ approveSupplierRequest }) => setApproved(approveSupplierRequest),
        onError: (error) => showBoundary(error),
    });
    const [decline, { loading: isDeclining }] = useMutation(declineSupplierRequestTemplate, {
        refetchQueries: ['incomingSupplierRequests'],
        onCompleted: () => setToDecline(undefined),
        onError: (error) => showBoundary(error),
    });

    const requests: SupplierRequest[] = data?.incomingSupplierRequests ?? [];
    if (isLoading && !data) return <Loader />;

    return (
        <Fragment>
            {/* The help texts explain the table; with no requests, "No requests yet" says it all. */}
            {requests.length ? <InfoText text={translate('client-directory:SUPPLIER_REQUESTS_INFO')} /> : undefined}
            {requests.length && !isAdmin ? (
                <InfoText text={translate('client-directory:SUPPLIER_REQUEST_ADMINS_ONLY')} />
            ) : undefined}
            <Box mt={2}>
                {requests.length ? (
                    <SupplierRequestTable
                        requests={pendingFirst(requests)}
                        labOf={(request) => request.requester}
                        renderActions={
                            isAdmin
                                ? (request) => (
                                      <Box display="flex" justifyContent="flex-end" gap={1}>
                                          <Button
                                              onClick={() => setToDecline(request)}
                                              disabled={isApproving || isDeclining}
                                              data-test={`supplier-requests.decline-${request._id}`}
                                          >
                                              {translate('client-directory:DECLINE_SUPPLIER_REQUEST')}
                                          </Button>
                                          <Button
                                              variant="contained"
                                              onClick={() => approve({ variables: { requestId: request._id } })}
                                              disabled={isApproving || isDeclining}
                                              data-test={`supplier-requests.approve-${request._id}`}
                                          >
                                              {translate('client-directory:APPROVE_SUPPLIER_REQUEST')}
                                          </Button>
                                      </Box>
                                  )
                                : undefined
                        }
                        dataTest="supplier-requests.incoming"
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {translate('client-directory:NO_SUPPLIER_REQUESTS')}
                    </Typography>
                )}
            </Box>

            <DeclineRequestDialog
                open={Boolean(toDecline)}
                partyName={toDecline?.requester.name}
                isDeclining={isDeclining}
                onClose={() => setToDecline(undefined)}
                onConfirm={(reason) => toDecline && decline({ variables: { requestId: toDecline._id, reason } })}
            />
            <ConfirmDialog
                open={Boolean(approved)}
                handleClose={() => setApproved(undefined)}
                dialogTitle={translate('client-directory:SUPPLIER_REQUEST_APPROVED_TITLE')}
                dialogContent={translate('client-directory:SUPPLIER_REQUEST_APPROVED_TEXT', {
                    lab: approved?.requester.name,
                })}
                confirmBtnLabel={translate('client-directory:SHOW_SUPPLIER_SETTINGS')}
                declineBtnLabel={translate('client-directory:NOT_NOW')}
                handleConfirm={() =>
                    approved && navigate(`/client-directory/clients/${approved.requester._id}/client-details`)
                }
            />
        </Fragment>
    );
};

export default IncomingSupplierRequests;
